import type { MutableRefObject } from 'react'
import type { MemoFlush } from '@/apps/latest/features/yakureki/components/MemoPanel'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Box, Paper } from '@mui/material'
import { Pause as PauseIcon } from '@mui/icons-material'
import { useQueryClient } from '@tanstack/react-query'

import { InterruptionRecoverableBanner } from '@/apps/latest/components/molecules/InterruptionRecoverableBanner'
import { InterruptionSavedBanner } from '@/apps/latest/components/molecules/InterruptionSavedBanner'
import { InterruptionUnrecoverableBanner } from '@/apps/latest/components/molecules/InterruptionUnrecoverableBanner'
import { InterventionRecommendations } from '@/apps/latest/components/molecules/InterventionRecommendations'
import { RecordingFailedAlert } from '@/apps/latest/components/molecules/RecordingFailedAlert'
import { SlowTranscriptionBanner } from '@/apps/latest/components/molecules/SlowTranscriptionBanner'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { useReleaseChannel } from '@/features/auth'
import { UI_FONT_SIZE } from '@/shared/styles/typography'
import type { RecordingViewModel } from '@/apps/latest/shared/recording-status/types'
import type { YakurekiStatus } from '@/shared/types/yakurekiCore'
import type { Highlight } from '@/shared/types/highlight'

import { Body } from './Body'
import { Header } from './Header'
import {
  MeetingTabBar,
  useDefaultMeetingTabs,
  type MeetingTab,
} from './MeetingTabBar'
import type { TabItem } from '@/components/molecules/TabBar'
import { RADIUS } from '@/shared/styles/radii'
import { usePollStreamingTranscript } from './usePollStreamingTranscript'
import { useRecordingFavicon } from './useRecordingFavicon'

interface Props {
  yakurekiId: string | null
  // L3 view-model. page (= IndexPage / recording-new / recording-group) が `useRecordingStatus`
  // で組み立て、 全 sibling (GuidancePrepPane / PatientInfoBar) と共有.
  recordingStatus: RecordingViewModel
  // 最終 SOAP 確定 callback. page 側で polling 停止 / invalidate 等に使う.
  onSoapFinalized?: (status: YakurekiStatus) => void
  /**
   * 低速文字起こし notice の外部 hint. engine.isSlowTranscription は hook instance に
   * 閉じるため、 録音した page から別 page へ遷移すると失われる (= フリー録音の
   * /soap/new → /soap/:id). 遷移元が history state で運んだ値を page が渡す.
   * engine 自身の flag と OR で効く.
   */
  slowTranscriptionHint?: boolean
  /**
   * 上記 hint の 1 bit 版 (log/207): 遷移元の録音が「意図した batch」だったか.
   * slowTranscriptionHint と同じ理由 (engine が instance に閉じる) で history state 経由.
   * notice の文言分岐にだけ使う.
   */
  intendedBatchHint?: boolean
  // memo タブが開いている間だけ MemoPanel の flush 関数が載る箱. page が「メモを確実に
  // 保存してから読む」ために使う (= メモから SOAP 生成. 未保存のまま生成すると back が
  // 古い memo_items を読む).
  memoFlushRef?: MutableRefObject<MemoFlush | null>
  /**
   * タブの並び。省略時は薬剤師の既定 (ハイライト始まり、 添付資料は β・件数つき)。
   * 最初に開くタブは並びの先頭。呼び出し側が決めるだけで、 ブロック自身は系統を知らない。
   */
  tabs?: TabItem<MeetingTab>[]
}

const formatElapsed = (sec: number): string => {
  const m = Math.floor(sec / 60)
  const s = Math.floor(sec % 60)
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}

export const MeetingBlock = ({
  yakurekiId,
  recordingStatus,
  onSoapFinalized,
  slowTranscriptionHint = false,
  intendedBatchHint = false,
  memoFlushRef,
  tabs,
}: Props) => {
  const defaultTabs = useDefaultMeetingTabs(yakurekiId)
  const tabItems = tabs ?? defaultTabs
  const [activeTab, setActiveTab] = useState<MeetingTab>(
    () => tabItems[0]?.key ?? 'highlight'
  )
  const [elapsedSec, setElapsedSec] = useState(0)

  const { status, errorBannerKind, showRetryCta, engine } = recordingStatus
  useRecordingFavicon(recordingStatus.isMicActive)

  // 「今確認すること」(intervention_recommendations) を出す gate。α channel (= 社内検証者) のみ。
  // α は見出しの「記録」の行を持たず、 経過時間をタブ行の右端に置く (yakureki-back log/262 決定 17・log/267).
  const { isAlpha } = useReleaseChannel()
  const showIntervention = isAlpha

  // 経過秒. recording のあいだ 1s tick で増やす. 録音終了/再録音間で 0 リセットは
  // derived display + ref clear で表現 (= useEffect 内 setState を避ける).
  const elapsedRef = useRef(0)
  useEffect(() => {
    elapsedRef.current = elapsedSec
  }, [elapsedSec])
  const isRecording = status === 'recording'
  const isPaused = status === 'paused'
  // 確定済みセグメントの duration 合計 (log/185 サーバ復元). 同一マウント中の pause では
  // elapsedSec がそのまま残るが、 患者切替 / reload / 再起動から復元 (adopt) した場合は
  // elapsedSec=0 のため、 サーバ由来の staged 合計を経過表示とタイマー再開起点に使う.
  const stagedFloor = Math.floor(engine.stagedDurationS || 0)
  useEffect(() => {
    if (!isRecording) {
      // 一時停止中 (log/185) は値を保持し、 resume で続きから加算する (= pause 中の
      // 実時間は録音長に含めない). それ以外の非録音状態で 0 リセット.
      if (!isPaused) {
        elapsedRef.current = 0
      }
      return
    }
    const startedAt =
      Date.now() - Math.max(elapsedRef.current, stagedFloor) * 1000
    const id = window.setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - startedAt) / 1000))
    }, 1000)
    return () => window.clearInterval(id)
  }, [isRecording, isPaused, stagedFloor])
  // paused / recording とも staged 合計 (サーバ復元由来) を下限にする.
  // recording でも max を取るのは、 復元 paused → 再開直後に setInterval の初回 tick
  // (+1000ms) まで elapsedSec が 0 のままで 00:00 がちらつくのを防ぐため (検証 B5).
  // elapsedSec が追い付けば max は無害. ref は render 中に読まない.
  const displayElapsed =
    isRecording || isPaused ? Math.max(elapsedSec, stagedFloor) : 0

  // L1 (server canonical event) は page が usePollEvent で 1 本だけ持ち、 recordingStatus
  // 経由で降りてくる. ここで自前の query を張ると同じ event を 2 つの observer が別 cadence
  // で観測することになり、 片方が先にアンマウントされた瞬間に polling が消える
  // (= 在宅サイドカーの取り逃し. log/186).
  const polledEvent = recordingStatus.event

  // 録音中 (status='recording'/'stopping') は Chirp の即時 finals を polling で取り込み、
  // transcript tab に逐次反映する.
  // paused 中も enabled にする: 患者切替から戻ってきた時 (= adoptPausedSession 復元後) に
  // 確定済みセグメントの finals をサーバから再取得して transcript tab を復元する (log/185).
  const streamingTranscript = usePollStreamingTranscript({
    sessionId: engine.sessionId,
    enabled:
      status === 'recording' || status === 'paused' || status === 'stopping',
  })

  const liveTranscript = useMemo(() => {
    const finals = streamingTranscript.data?.transcript.finals ?? []
    if (finals.length === 0) return ''
    return finals.map((f) => f.text).join('\n')
  }, [streamingTranscript.data])

  const liveInterim = streamingTranscript.data?.transcript.interim?.text ?? ''

  const highlights: Highlight[] = useMemo(
    () => polledEvent?.highlights ?? [],
    [polledEvent]
  )

  const yakurekiStatus: YakurekiStatus | undefined = polledEvent?.status

  // status 変化時に Sidebar の最近薬歴 list を invalidate (= refetch).
  // queryKey ['yakurekiList'] prefix matches sidebar / landing 両方を invalidate.
  const queryClient = useQueryClient()
  const prevStatusRef = useRef<YakurekiStatus | undefined>(undefined)
  useEffect(() => {
    if (yakurekiStatus !== prevStatusRef.current) {
      prevStatusRef.current = yakurekiStatus
      queryClient.invalidateQueries({ queryKey: ['yakurekiList'] })
    }
  }, [yakurekiStatus, queryClient])

  // 最終 SOAP 確定 callback. justCompleted (= viewModel 1 render flag) で発火.
  useEffect(() => {
    if (recordingStatus.justCompleted && yakurekiStatus !== undefined) {
      onSoapFinalized?.(yakurekiStatus)
    }
  }, [recordingStatus.justCompleted, yakurekiStatus, onSoapFinalized])

  // 経過時間は録音中のみ出す (= 各タブには出さない / 松田指示). 置き場は ga = 見出しの「記録」の横、
  // β = タブ行の右端 (指導前準備のカードと同じく、 タブ行から始める. yakureki-back log/262 決定 15・17).
  const elapsedLabel = formatElapsed(displayElapsed)
  const elapsedSlot =
    isRecording || isPaused ? (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          fontSize: UI_FONT_SIZE.micro,
          color: colorTokens.fg.muted,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {/* paused はタイマー凍結だけでは録音中と区別しづらいため、 既存のタイマー
            slot に ⏸ glyph を添えて示す (面を増やさない 1 情報 1 箇所の範囲). */}
        {isPaused && <PauseIcon sx={{ fontSize: 14 }} />}
        {elapsedLabel}
      </Box>
    ) : undefined

  // 録音中の破棄導線は出さない (= 録音中は停止ボタンのみ. 松田指示). 破棄は
  // 一時停止中 (PatientInfoBar の [破棄], ADR-0018) と SOAP 生成後
  // (PostRecordingView の ⋯ → 録音を削除 = detach-nsips) の 2 箇所に限る.
  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: `${RADIUS.xl}px`, overflow: 'hidden' }}
    >
      {!isAlpha && <Header rightSlot={elapsedSlot} />}
      <MeetingTabBar
        active={activeTab}
        onChange={setActiveTab}
        status={status}
        stream={engine.stream}
        tabs={tabItems}
        rightSlot={isAlpha ? elapsedSlot : undefined}
      />
      {/* paused (in-flight 含む) をブロック本文で重ねて表現しない: 状態テキストは
          患者名行 caption (一時停止中 / 録音を保存中… / 再開中…)、操作 feedback は
          PatientInfoBar CTA slot の glyph 入り ring 1 個、ブロック側は経過時間の slot (ga = Header 右端 /
          β = タブ行右端) の凍結タイマー + ⏸ glyph が担う (引き算原則の重複描画 1 本化. info 色 Alert は
          callout-banner の Don't. block tint は不採用 = recording も白のままの kakeru 意匠). */}
      {errorBannerKind === 'recoverable' && (
        <Box sx={{ px: 2, pt: 2 }}>
          <InterruptionRecoverableBanner />
        </Box>
      )}
      {errorBannerKind === 'unrecoverable' && (
        <Box sx={{ px: 2, pt: 2 }}>
          <InterruptionUnrecoverableBanner />
        </Box>
      )}
      {errorBannerKind === 'interrupted-saved' && (
        <Box sx={{ px: 2, pt: 2 }}>
          <InterruptionSavedBanner />
        </Box>
      )}
      {errorBannerKind === 'finalize-failed' && (
        <Box sx={{ px: 2, pt: 2 }}>
          <RecordingFailedAlert
            errorMessage={recordingStatus.errorMessage ?? ''}
            hasPendingBlob={showRetryCta}
            isRetryHopeless={recordingStatus.isRetryHopeless}
            onRetry={() => void engine.retry()}
            onDownload={engine.downloadBlob}
            retrying={engine.state === 'finalizing'}
          />
        </Box>
      )}
      {errorBannerKind === 'none' && recordingStatus.errorMessage && (
        <Box sx={{ px: 2, pt: 2 }}>
          <Alert severity="error">{recordingStatus.errorMessage}</Alert>
        </Box>
      )}
      {/* 低速文字起こし notice: batch fallback (backend 宣言 / client 起点 fallback) で
          SOAP 生成が legacy batch で走っている間 (= generating) に出す.
          録音中 (recording) は黄バナー (recoverable) が担当し、 完了 (completed) で自然に
          消える. error banner とは独立に併記可. */}
      {status === 'generating' &&
        (engine.isSlowTranscription || slowTranscriptionHint) && (
          <Box sx={{ px: 2, pt: 2 }}>
            <SlowTranscriptionBanner
              isIntendedBatch={engine.isIntendedBatch || intendedBatchHint}
            />
          </Box>
        )}
      <Body
        memoFlushRef={memoFlushRef}
        recordingStatus={recordingStatus}
        activeTab={activeTab}
        highlights={highlights}
        event={polledEvent ?? null}
        yakurekiId={yakurekiId}
        liveTranscript={liveTranscript}
        liveInterim={liveInterim}
      />
      {/* 「今確認すること」は記録ブロック最下部に固定 (= タブ内容と分離し、
          どのタブでも block 下端に常駐)。空なら component 側で非表示。 */}
      {showIntervention && (
        <InterventionRecommendations
          recommendations={polledEvent?.intervention_recommendations ?? []}
        />
      )}
    </Paper>
  )
}
